/**
 * projectRiskConverters.js
 *
 * DashboardProjectRiskPage から抽出された純粋関数群
 * プロジェクト別リスク画面固有のデータ変換・マトリックス生成ロジックを提供
 */

import {
  toDate,
  normalizeImpactLevel,
  countByImpactLevel,
  groupDetailsByImpact,
  isRowCompleted,
  makeTaskDetail,
  calcTaskData,
} from './regionRiskConverters.js';

// 再エクスポート（ページで直接使用するため）
export {
  toDate,
  normalizeImpactLevel,
  countByImpactLevel,
  groupDetailsByImpact,
  isRowCompleted,
  makeTaskDetail,
  calcTaskData,
};

/**
 * 大項目順序定義（プロセス順）
 */
export const LARGE_CATEGORY_ORDER = [
  'プリセールス',
  'KDDI要件定義',
  '契約',
  'SIM',
  'サービスイン判定',
  '設計',
  '開発',
  '試験',
  'ラボリリース',
  '車載器試験',
  'NW ready',
  '商用リリース',
  'FS',
  'CV',
  '1A',
  '設備引継ぎ',
  '運用引継ぎ条件書',
  '運用サービスイン判定',
  'サービス運用引継ぎ',
  'プレ運用/運用トライアル',
  'LO',
  '運用開始',
  'EDER',
  '初回ADF',
];

/**
 * OEM×対地ごとに大項目（プロセス）を集計してマトリックス構造を生成
 * @param {Array<Object>} schedules - スケジュール配列
 * @param {Array<Object>} problems - 課題配列
 * @param {Array<Object>} risks - リスク配列
 * @returns {Object} {columns: Array<string>, regionData: Array<Object>}
 */
export function buildRegionMatrix(schedules, problems, risks) {
  const cellMap = new Map();
  const oemRegionSet = new Set();
  const largeCatSet = new Set();

  schedules.forEach((item) => {
    const oem = item.oem_region || '';
    const lc = item.large_category_name || '';
    if (!oem || !lc) return;
    oemRegionSet.add(oem);
    largeCatSet.add(lc);
    const key = `${oem}|${lc}`;
    if (!cellMap.has(key)) cellMap.set(key, { schedules: [], problems: [], risks: [] });
    cellMap.get(key).schedules.push(item);
  });

  problems.forEach((item) => {
    const oem = item.oemRegion || '';
    const lc = item.largeCategory || '';
    if (!oem || !lc) return;
    oemRegionSet.add(oem);
    largeCatSet.add(lc);
    const key = `${oem}|${lc}`;
    if (!cellMap.has(key)) cellMap.set(key, { schedules: [], problems: [], risks: [] });
    cellMap.get(key).problems.push(item);
  });

  risks.forEach((item) => {
    const oem = item.oemRegion || '';
    const lc = item.largeCategory || '';
    if (!oem || !lc) return;
    oemRegionSet.add(oem);
    largeCatSet.add(lc);
    const key = `${oem}|${lc}`;
    if (!cellMap.has(key)) cellMap.set(key, { schedules: [], problems: [], risks: [] });
    cellMap.get(key).risks.push(item);
  });

  const orderedCols = LARGE_CATEGORY_ORDER.filter((cat) => largeCatSet.has(cat));
  const remainingCols = Array.from(largeCatSet).filter(
    (cat) => !LARGE_CATEGORY_ORDER.includes(cat),
  );
  const columns = [...orderedCols, ...remainingCols];

  const oemRegions = Array.from(oemRegionSet).sort((a, b) => a.localeCompare(b, 'ja'));

  const emptyCell = {
    taskCounts: { total: 0, planned: 0, completed: 0, delayed: 0 },
    taskDetails: { total: [], planned: [], completed: [], delayed: [] },
    issueCounts: { H: 0, M: 0, L: 0 },
    riskCounts: { H: 0, M: 0, L: 0 },
    issueDetails: { H: [], M: [], L: [] },
    riskDetails: { H: [], M: [], L: [] },
  };

  const regionData = oemRegions.map((oemRegion) => {
    const processColumns = columns.map((largeCat) => {
      const bucket = cellMap.get(`${oemRegion}|${largeCat}`);
      if (!bucket) return { processName: largeCat, ...emptyCell };

      const { counts: taskCounts, details: taskDetails } = calcTaskData(bucket.schedules);

      const activeProblems = bucket.problems.filter((p) => {
        const st = p.status_name || p.status || p.statusName || '';
        return String(st) !== '完了' && String(st).toLowerCase() !== 'done' && !p.todo;
      });
      const activeRisks = bucket.risks.filter((r) => !isRowCompleted(r));

      const issueCounts = countByImpactLevel(activeProblems, 'impact');
      const riskCounts = countByImpactLevel(activeRisks, 'riskLevel');
      const issueDetails = groupDetailsByImpact(activeProblems, 'summary', 'problem', 'impact');
      const riskDetails = groupDetailsByImpact(activeRisks, 'title', 'risk', 'riskLevel');
      return { processName: largeCat, taskCounts, taskDetails, issueCounts, riskCounts, issueDetails, riskDetails };
    });
    return { oemRegion, processColumns };
  });

  return { columns, regionData };
}
